import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { BlockedGeosSelector } from "components/general/BlockedGeosSelector"
import { WelcomeSection } from "components/general/WelcomeSection"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { Tabs } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"
import { CryptoCoinsTab } from "components/settings/CryptoCoinsTab"
import { CurrenciesTab } from "components/settings/CurrenciesTab"
import { CustomerioTab } from "components/settings/CustomerioTab"
import { DangerZoneTab } from "components/settings/DangerZoneTab"
import { EmailTab } from "components/settings/EmailTab"
import { GeneralTab } from "components/settings/GeneralTab"
import { GiftsTab } from "components/settings/GiftsTab"
import { LanguagesTab } from "components/settings/LanguagesTab"
import { LimitsTab } from "components/settings/LimitsTab"
import { LiveChatTab } from "components/settings/LiveChatTab"
import { MaintenanceSettingsTab } from "components/settings/MaintenanceSettingsTab"
import { ResetToDefaultButton } from "components/settings/ResetToDefaultButton"
import { VerificationProfileTab } from "components/settings/VerificationProfileTab"
import { VipSettingsTab } from "components/settings/VipSettingsTab"
import { WithdrawalsTab } from "components/settings/WithdrawalsTab"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard"

import { PERMISSIONS } from "constants/permissions"
import { SETTINGS_TAB_ORDER, SettingsTab } from "constants/settings"

import { CMSService } from "services/cms/cms.service"
import { settingsService } from "services/settings/settings.service"
import { AdminSettingsWithVip, CryptoCoinsSettings } from "services/settings/type"

import { GameCardConfig, Tenant } from "types/api"

export const Settings: FC = (): ReactElement => {
  const { user, darkMode } = useProps()
  const { has } = usePermissions()
  const { t } = useTranslation("translation", { useSuspense: false })
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState<AdminSettingsWithVip>()
  const [cryptoCoins, setCryptoCoins] = useState<CryptoCoinsSettings | null>(null)
  const [gameCardConfig, setGameCardConfig] = useState<GameCardConfig | null>(null)
  const [currentTab, setCurrentTab] = useState<SettingsTab>(SettingsTab.Currencies)
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null)

  const { dirty, markDirty, markClean, guard, confirmPending, cancelPending, pending } = useUnsavedChangesGuard()

  const canSeeCustomerIo = has(PERMISSIONS.SETTINGS_CUSTOMERIO)
  const canSeeLiveChat = has(PERMISSIONS.SETTINGS_LIVE_CHAT)
  const canSeeMaintenance = has(PERMISSIONS.SETTINGS_MAINTENANCE)
  const canSeeFakeBets = has(PERMISSIONS.SETTINGS_FAKE_BETS)
  const canResetSettings = has(PERMISSIONS.SETTINGS_RESET)

  const tabs = useMemo<SettingsTab[]>(() => {
    const baseTabs: SettingsTab[] = [...SETTINGS_TAB_ORDER]
    if (canSeeCustomerIo) baseTabs.push(SettingsTab.CustomerIO)
    if (canSeeLiveChat) baseTabs.push(SettingsTab.LiveChat)
    if (canSeeMaintenance) baseTabs.push(SettingsTab.Maintenance)
    // Always last: it is the only place a destructive action lives.
    if (canResetSettings) baseTabs.push(SettingsTab.DangerZone)
    return baseTabs
  }, [canSeeCustomerIo, canSeeLiveChat, canSeeMaintenance, canResetSettings])

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)

      const base = await settingsService.getAll()
      const vip = await settingsService.getVip()
      const coins = await settingsService.getCryptoCoins()
      if (coins) setCryptoCoins(coins)

      // Fetch current tenant data based on env variable
      const tenantId = process.env.REACT_APP_TENANT
      if (tenantId) {
        const tenants = await CMSService.getTenants()
        const tenant = tenants?.find((item) => item.id === tenantId)
        if (tenant) {
          setCurrentTenant(tenant)
          setGameCardConfig(tenant.setup_data.gameCardConfig || null)
        }
      }

      if (base) {
        setSettings({
          ...base,
          vip,
          gifts: base.gifts ?? { enabled: true, minDepositCents: 10000, minSendCents: 100, maxSendCents: 0 }
        })
      }

      markClean()
    } catch {
      toast.error(t("settings.loadFailed") as string)
    } finally {
      setLoading(false)
    }
  }, [markClean, t])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  /** Every tab edits local state; wrap the setter so leaving the tab can warn about it. */
  const editSettings = useCallback(
    (update: (prev: AdminSettingsWithVip) => AdminSettingsWithVip) => {
      markDirty()
      setSettings((prev) => prev && update(prev))
    },
    [markDirty]
  )

  const settingsTabLabels: Record<SettingsTab, string> = useMemo(
    () => ({
      [SettingsTab.Currencies]: t("settings.tabs.currencies"),
      [SettingsTab.CryptoCoins]: t("settings.tabs.cryptoCoins"),
      [SettingsTab.Languages]: t("settings.tabs.languages"),
      [SettingsTab.GEO]: t("settings.tabs.geo"),
      [SettingsTab.Email]: t("settings.tabs.email"),
      [SettingsTab.Withdrawals]: t("settings.tabs.withdrawals"),
      [SettingsTab.CustomerIO]: t("settings.tabs.customerio"),
      [SettingsTab.LiveChat]: t("settings.tabs.liveChat"),
      [SettingsTab.VIP]: t("settings.tabs.vip"),
      [SettingsTab.Maintenance]: t("settings.tabs.maintenance"),
      [SettingsTab.Limits]: t("settings.tabs.limits"),
      [SettingsTab.Gifts]: t("settings.tabs.gifts"),
      [SettingsTab.VerificationProfile]: t("settings.tabs.verificationProfile"),
      [SettingsTab.General]: t("settings.tabs.general"),
      [SettingsTab.DangerZone]: t("settings.tabs.dangerZone")
    }),
    [t]
  )

  const renderTabContent = () => {
    if (!settings) return null

    switch (currentTab) {
      case SettingsTab.Currencies:
        return (
          <CurrenciesTab
            currencies={settings.rcoin}
            onChange={(rcoin) => editSettings((prev) => ({ ...prev, rcoin }))}
            refreshSettings={fetchSettings}
            onSaved={markClean}
          />
        )

      case SettingsTab.CryptoCoins:
        if (!cryptoCoins) return null
        return <CryptoCoinsTab cryptoCoins={cryptoCoins} onChange={setCryptoCoins} refreshSettings={fetchSettings} />

      case SettingsTab.Languages:
        return (
          <LanguagesTab
            languages={settings.languages}
            localeUrls={settings.locale_urls}
            onChange={(languages) => editSettings((prev) => ({ ...prev, languages }))}
            refreshSettings={fetchSettings}
            onSaved={markClean}
          />
        )

      case SettingsTab.GEO:
        return (
          <BlockedGeosSelector
            blockedGeos={settings.blockedGeos}
            onChange={(geos) => editSettings((prev) => ({ ...prev, blockedGeos: geos }))}
            onSave={async () => {
              const success = await settingsService.updateBlockedGeos(settings.blockedGeos)
              if (success) markClean()
              return success
            }}
            actions={<ResetToDefaultButton target="blockedGeos" label={t("settings.tabs.geo")} onDone={fetchSettings} />}
          />
        )

      case SettingsTab.Email:
        return (
          <EmailTab
            auth={settings.auth}
            currentTenant={currentTenant}
            onChange={(auth) => setSettings((prev) => prev && { ...prev, auth })}
            onDirty={markDirty}
            onSaved={markClean}
            onTenantChange={setCurrentTenant}
            refreshSettings={fetchSettings}
          />
        )

      case SettingsTab.Withdrawals:
        return (
          <WithdrawalsTab
            withdrawals={settings.withdrawals}
            kyc={settings.kyc}
            onChange={(withdrawals) => editSettings((prev) => ({ ...prev, withdrawals }))}
            onKycChange={(kyc) => editSettings((prev) => ({ ...prev, kyc }))}
            refreshSettings={fetchSettings}
            onSaved={markClean}
          />
        )

      case SettingsTab.Limits:
        return (
          <LimitsTab
            limits={settings.limits}
            currencies={settings.rcoin}
            onChange={(limits) => editSettings((prev) => ({ ...prev, limits }))}
            refreshSettings={fetchSettings}
            onSaved={markClean}
          />
        )

      case SettingsTab.VIP:
        return (
          <VipSettingsTab
            vip={settings.vip}
            onChange={(next) => editSettings((prev) => ({ ...prev, vip: next }))}
            refreshSettings={fetchSettings}
            onSave={async () => {
              const success = await settingsService.updateVip(settings.vip)
              if (success) {
                toast.success(t("settings.vip.toastUpdated") as string)
                markClean()
              }
            }}
          />
        )

      case SettingsTab.Maintenance:
        if (!canSeeMaintenance) return null
        return <MaintenanceSettingsTab enabledLanguages={settings.languages} />

      case SettingsTab.CustomerIO:
        if (!canSeeCustomerIo) return null
        return <CustomerioTab />

      case SettingsTab.LiveChat:
        if (!canSeeLiveChat) return null
        return <LiveChatTab tenant={currentTenant} />

      case SettingsTab.Gifts:
        return (
          <GiftsTab
            gifts={settings.gifts}
            onChange={(gifts) => editSettings((prev) => ({ ...prev, gifts }))}
            refreshSettings={fetchSettings}
            onSaved={markClean}
          />
        )

      case SettingsTab.VerificationProfile:
        return (
          <VerificationProfileTab
            verification_options={settings.verification_options}
            onChange={(verification_options) => editSettings((prev) => ({ ...prev, verification_options }))}
            refreshSettings={fetchSettings}
            onSaved={markClean}
          />
        )

      case SettingsTab.General:
        return (
          <GeneralTab
            betsTables={settings.bets_tables}
            currencies={settings.rcoin}
            gameCardConfig={gameCardConfig}
            onBetsTablesChange={(bets_tables) => editSettings((prev) => ({ ...prev, bets_tables }))}
            onGameCardConfigChange={setGameCardConfig}
            onRefresh={fetchSettings}
            canSeeFakeBets={canSeeFakeBets}
            vipTiers={settings.vip?.tiers ?? []}
          />
        )

      case SettingsTab.DangerZone:
        if (!canResetSettings) return null
        return <DangerZoneTab onRefresh={fetchSettings} tabLabels={settingsTabLabels} />

      default:
        return null
    }
  }

  return (
    <div className={clsx(darkMode && "dark")}>
      <div className="grid w-full gap-8 p-4 pt-0 sm:p-8 sm:pt-0 grid-col-1">
        <TopBar title={t("pages.settings.title")} />
        {user && <WelcomeSection />}

        <Tabs
          tabs={tabs.map((tab) => ({ key: tab, label: settingsTabLabels[tab] || tab }))}
          activeTab={currentTab}
          onChange={(key) => guard(() => setCurrentTab(key as SettingsTab))}
          variant="pills"
        />

        {dirty && <p className="text-sm text-warning">{t("settings.unsaved.notice")}</p>}

        {loading ? (
          <p className="text-[rgba(6,7,21,1)] dark:text-white">{t("common.loading")}</p>
        ) : settings ? (
          renderTabContent()
        ) : (
          <p className="text-gray-700 dark:text-gray-300">{t("settings.noSettingsFound")}</p>
        )}
      </div>

      {pending && (
        <ModalConfirmAction
          data={pending}
          handler={cancelPending}
          text={t("settings.unsaved.confirm")}
          actionText={t("settings.unsaved.discard")}
          actionHandler={confirmPending}
          actionClassName="btn-error"
        />
      )}
    </div>
  )
}
